import { KeyboardEvent, MouseEvent, ReactNode, memo, useCallback } from 'react';
import css from './GenericClickable.module.scss';
import { GenericClickableProps } from './GenericClickable.types';
import classNames from 'classnames';

/**
 * A generic button component that wraps other elements or text to make them able to be interacted with and
 * to perform onClick actions.
 *
 * @param onClick - {@link GenericClickableProps.onClick onClick} description.
 * @param className - {@link GenericClickableProps.className className} description.
 * @param tabIndex - {@link GenericClickableProps.tabIndex tabIndex} description.
 * @param title - {@link GenericClickableProps.title title} description.
 * @param children - {@link GenericClickableProps.children children} description.
 */
export const GenericClickable = memo(({
    onClick,
    className,
    tabIndex,
    title,
    children,
}: GenericClickableProps): ReactNode => {
    const onClickHelper = useCallback((event: MouseEvent<HTMLDivElement>): void => {
        event.stopPropagation();

        if (onClick) {
            onClick(event);
        }
    }, [onClick]);
    const onKeyboardClick = useCallback((event: KeyboardEvent<HTMLDivElement>): void => {
        if (['Enter'].includes(event.key)) {
            event.stopPropagation();

            if (onClick) {
                onClick(event);
            }
        }
    }, [onClick]);

    return (
        <div
            role={'button'}
            className={classNames(css.genericClickable, className)}
            tabIndex={tabIndex ?? 0}
            onClick={onClickHelper}
            onKeyDown={onKeyboardClick}
            title={title}
        >
            {children}
        </div>
    );
});
